import React, { useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import type { Feature, Geometry } from 'geojson';
import {
  Alert,
  Box,
  Chip,
  Fade,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  AUTH_QUERY,
  CREATE_OBJECT_MUTATION,
  DELETE_OBJECT_MUTATION,
  GET_MY_OBJECTS_QUERY,
  GET_MY_OBJECT_GROUPS_QUERY,
  GET_MY_SITES_QUERY,
  UPDATE_OBJECT_MUTATION,
} from '../../../gql';
import { RequiredLabel } from '../../RequiredLabel';
import { useSettings } from '../../context/SettingsContext/useSettings';
import { useWorkspace } from '../../context/WorkspaceContext/useWorkspace';
import { MapTools } from '../../context/WorkspaceContext';
import {
  layerLabels,
  layers as availableLayers,
  objectTypes,
  parentObjectTypes,
  isParentType,
} from '../../enums';
import {
  estimateCircleRadiusMeters,
  isCircleLikePolygon,
  longestSideMeters,
  openPolygonRing,
  polygonAreaSquareMeters,
} from '../../MapView/geo';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useVocabulary } from '../../../i18n';
import {
  Autocomplete,
  Button,
  CondensedDangerZoneAction,
  FieldLabel,
  FormField,
  Switch,
  SwitchLabel,
  TextField,
} from '../../design-system';

const inputStyles = { width: '100%' };
const switchLabelStyles = {
  '& .MuiFormControlLabel-label': { fontSize: 13 },
};
const copyButtonStyles = {
  p: 0.25,
  width: 22,
  height: 22,
};
const infoButtonStyles = {
  p: 0.25,
  width: 22,
  height: 22,
  color: 'grey.600',
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'primary.main',
  },
};
const infoTooltipSlotProps = {
  tooltip: {
    sx: {
      backgroundColor: 'grey.900',
      color: 'common.white',
      opacity: '1 !important',
      boxShadow: 3,
      p: 1,
      maxWidth: 260,
    },
  },
  arrow: {
    sx: {
      color: 'grey.900',
    },
  },
};
const idCopiedStyles = { fontSize: 11, fontWeight: 500, color: 'success.main' };

function formatDate(input?: string | null): string | null {
  if (!input) return null;
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

function formatArea(sqm: number): string {
  const ha = sqm / 10000;
  const formattedSqm = sqm >= 100 ? Math.round(sqm) : sqm.toFixed(1);
  const formattedHa = ha >= 1 ? ha.toFixed(2) : ha.toFixed(4);
  return `${formattedSqm} m² (${formattedHa} ha)`;
}

function formatMeters(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`;
}

interface GeometryStats {
  areaLabel?: string;
  longestSideLabel?: string;
  diameterLabel?: string;
  shape?: 'polygon' | 'circle';
}

function computeGeometryStats(
  geometry: Geometry | null | undefined
): GeometryStats | null {
  if (!geometry || geometry.type !== 'Polygon') return null;
  const rawRing = (geometry as any).coordinates?.[0] as
    | [number, number][]
    | undefined;
  if (!rawRing || rawRing.length < 3) return null;
  const openRing = openPolygonRing(rawRing);

  const area = polygonAreaSquareMeters(rawRing);
  const areaLabel = area > 0 ? formatArea(area) : undefined;

  if (isCircleLikePolygon(openRing)) {
    const radius = estimateCircleRadiusMeters(openRing);
    return {
      shape: 'circle',
      areaLabel,
      diameterLabel: radius > 0 ? formatMeters(radius * 2) : undefined,
    };
  }

  const longest = longestSideMeters(rawRing);
  return {
    shape: 'polygon',
    areaLabel,
    longestSideLabel: longest > 0 ? formatMeters(longest) : undefined,
  };
}

const layerKeys = Object.keys(availableLayers);
const getLayerLabel = (key: string) => layerLabels[key] ?? key;

interface SiteOption {
  id: string;
  name: string;
}

interface GroupOption {
  id: string;
  name?: string | null;
  shared?: boolean | null;
  level?: number | null;
}

interface ObjectSourceRecord {
  id: string;
  name?: string | null;
  description?: string | null;
  type?: string | null;
  siteId?: string | null;
  siteStatus?: 'none' | 'accessible' | 'restricted';
  groupId?: string | null;
  parentId?: string | null;
  parentName?: string | null;
  mapsVisible?: string[] | null;
  filled?: boolean | null;
  visibility?: string | null;
  geometry?: Geometry | null;
  position?: Geometry | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  createdBy?: string | null;
  createdByName?: string | null;
  createdByEmail?: string | null;
}

interface ParentOption {
  id: string;
  name?: string | null;
  type?: string | null;
  siteId?: string | null;
}

type FormValues = {
  name: string;
  description: string;
  isParent: boolean;
  type: string | null;
  site: SiteOption | null;
  group: GroupOption | null;
  parent: ParentOption | null;
  mapsVisible: string[];
  filled: boolean;
};

const emptyDefaults: FormValues = {
  name: '',
  description: '',
  isParent: false,
  type: null,
  site: null,
  group: null,
  parent: null,
  mapsVisible: [],
  filled: false,
};

// Best-effort centroid for the label under the form. The server recomputes the
// authoritative centroid via ST_Centroid on save; this is purely informational.
function computeCenter(
  geometry: Geometry | null | undefined
): {
  lng: number;
  lat: number;
} | null {
  if (!geometry) return null;
  const coords: [number, number][] = [];
  const collect = (c: any) => {
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      coords.push([c[0], c[1]]);
    } else if (Array.isArray(c)) {
      c.forEach(collect);
    }
  };
  collect((geometry as any).coordinates);
  if (coords.length === 0) return null;
  const lng = coords.reduce((s, c) => s + c[0], 0) / coords.length;
  const lat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
  return { lng, lat };
}

const geometryMessages = defineMessages({
  Point: { id: 'map.objects.geometry.point', defaultMessage: 'point' },
  LineString: { id: 'map.objects.geometry.line', defaultMessage: 'line' },
  Polygon: { id: 'map.objects.geometry.polygon', defaultMessage: 'polygon' },
});

const infoTooltipRowStyles = { fontSize: 12, lineHeight: 1.35 };
const infoTooltipLabelStyles = {
  fontSize: 11,
  fontWeight: 600,
  opacity: 0.75,
  mt: 0.25,
};

const InfoTooltipCard = ({ existing }: { existing: ObjectSourceRecord }) => {
  const geometry = existing.geometry ?? existing.position ?? null;
  const stats = useMemo(() => computeGeometryStats(geometry), [geometry]);

  const creator =
    existing.createdByName ||
    existing.createdByEmail ||
    existing.createdBy ||
    null;
  const createdOn = formatDate(existing.createdAt);
  const updatedOn = formatDate(existing.updatedAt);

  return (
    <Box sx={{ py: 0.5, minWidth: 200 }}>
      {creator && (
        <>
          <Typography sx={infoTooltipLabelStyles}>
            <FormattedMessage
              id="map.objects.createdBy"
              defaultMessage="Created by"
            />
          </Typography>
          <Typography sx={infoTooltipRowStyles}>{creator}</Typography>
        </>
      )}
      {createdOn && (
        <>
          <Typography sx={infoTooltipLabelStyles}>
            <FormattedMessage
              id="map.objects.createdOn"
              defaultMessage="Created on"
            />
          </Typography>
          <Typography sx={infoTooltipRowStyles}>{createdOn}</Typography>
        </>
      )}
      {updatedOn && updatedOn !== createdOn && (
        <>
          <Typography sx={infoTooltipLabelStyles}>
            <FormattedMessage
              id="map.objects.updatedOn"
              defaultMessage="Last update on"
            />
          </Typography>
          <Typography sx={infoTooltipRowStyles}>{updatedOn}</Typography>
        </>
      )}
      {stats?.areaLabel && (
        <>
          <Typography sx={infoTooltipLabelStyles}>
            <FormattedMessage id="map.objects.area" defaultMessage="Area" />
          </Typography>
          <Typography sx={infoTooltipRowStyles}>{stats.areaLabel}</Typography>
        </>
      )}
      {stats?.longestSideLabel && (
        <>
          <Typography sx={infoTooltipLabelStyles}>
            <FormattedMessage
              id="map.objects.longestSide"
              defaultMessage="Longest side"
            />
          </Typography>
          <Typography sx={infoTooltipRowStyles}>
            {stats.longestSideLabel}
          </Typography>
        </>
      )}
      {stats?.diameterLabel && (
        <>
          <Typography sx={infoTooltipLabelStyles}>
            <FormattedMessage
              id="map.objects.diameter"
              defaultMessage="Diameter"
            />
          </Typography>
          <Typography sx={infoTooltipRowStyles}>
            {stats.diameterLabel}
          </Typography>
        </>
      )}
    </Box>
  );
};

interface ObjectFormProps {
  existing?: ObjectSourceRecord | null;
}

const ObjectForm = ({ existing }: ObjectFormProps) => {
  const intl = useIntl();
  const getVocabularyLabel = useVocabulary();
  const {
    store: { objectDraft, editingObjectId, activeMapTool },
    clearObjectDraft,
    setActiveMapTool,
    getWorkspaceStore,
  } = useWorkspace();
  const { setSelectedObject } = useSettings();
  const [idCopied, setIdCopied] = useState(false);

  const { data: authData } = useQuery(AUTH_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const viewerId: string | undefined = authData?.viewer?.user?.id;

  const { data: sitesData } = useQuery(GET_MY_SITES_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const sites: SiteOption[] = sitesData?.getMySites ?? [];

  const { data: groupsData } = useQuery(GET_MY_OBJECT_GROUPS_QUERY, {
    fetchPolicy: 'cache-first',
  });
  // Only own groups or ones I've been given level >= 2 access to are eligible
  // for assigning objects into. Shared read-only (level 0/1) is intentionally
  // excluded from this picker.
  const allGroups: GroupOption[] = groupsData?.getMyObjectGroups ?? [];
  const groups: GroupOption[] = useMemo(
    () => allGroups.filter((g) => !g.shared || (g.level ?? -1) >= 2),
    [allGroups]
  );

  const { data: objectsData } = useQuery(GET_MY_OBJECTS_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const parentOptions: ParentOption[] = useMemo(() => {
    const all = objectsData?.getMyObjects ?? [];
    return all
      .filter(
        (o: ObjectSourceRecord) =>
          isParentType(o.type) && o.id !== editingObjectId
      )
      .map((o: ObjectSourceRecord) => ({
        id: o.id,
        name: o.name,
        type: o.type,
        siteId: o.siteId ?? null,
      }));
  }, [objectsData, editingObjectId]);

  const defaultValues: FormValues = useMemo(() => {
    if (!existing) return emptyDefaults;
    return {
      name: existing.name ?? '',
      description: existing.description ?? '',
      isParent: isParentType(existing.type),
      type: existing.type ?? null,
      site: sites.find((s) => s.id === existing.siteId) ?? null,
      group:
        allGroups.find((g) => g.id === existing.groupId) ??
        (existing.groupId
          ? { id: existing.groupId, name: existing.groupId }
          : null),
      parent: (() => {
        if (!existing.parentId) return null;
        const fromOptions = parentOptions.find(
          (p) => p.id === existing.parentId
        );
        if (fromOptions) return fromOptions;
        const fromAll = (objectsData?.getMyObjects ?? []).find(
          (o: ObjectSourceRecord) => o.id === existing.parentId
        );
        return {
          id: existing.parentId,
          name: existing.parentName ?? fromAll?.name ?? existing.parentId,
          siteId: fromAll?.siteId ?? null,
        };
      })(),
      mapsVisible: existing.mapsVisible ?? [],
      filled: Boolean(existing.filled),
    };
  }, [existing, sites, allGroups, parentOptions, objectsData]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { dirtyFields, isSubmitting },
  } = useForm<FormValues>({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const isParent = useWatch({ control, name: 'isParent' });
  const currentType = useWatch({ control, name: 'type' });
  const selectedParent = useWatch({ control, name: 'parent' });
  const siteLockedToParent = !isParent && !!selectedParent;
  const canEditSite = !existing || existing.createdBy === viewerId;
  const siteRestricted = existing?.siteStatus === 'restricted';

  const typeOptions = useMemo(
    () =>
      isParent
        ? ((parentObjectTypes as unknown) as string[])
        : ((objectTypes as unknown) as string[]),
    [isParent]
  );
  const typeLabelFor = (value: string) =>
    getVocabularyLabel('objectType', value);

  // Keep the type selection consistent with the parent switch. When the switch
  // flips, drop a value that doesn't belong to the new option list; also clear
  // fields that don't apply to parents (parent picker, group).
  useEffect(() => {
    if (currentType && !typeOptions.includes(currentType)) {
      setValue('type', null, { shouldDirty: true });
    }
    if (isParent) {
      setValue('parent', null, { shouldDirty: true });
      setValue('group', null, { shouldDirty: true });
      setValue('filled', true, { shouldDirty: true });
    }
  }, [isParent, currentType, typeOptions, setValue]);

  // Child objects inherit site from their parent — keep the form field in sync
  // and locked so parent/child sites can't drift apart.
  useEffect(() => {
    if (!siteLockedToParent || !selectedParent) return;
    const parentSite = selectedParent.siteId
      ? sites.find((s) => s.id === selectedParent.siteId) ?? null
      : null;
    setValue('site', parentSite, {
      shouldDirty: selectedParent.id !== existing?.parentId,
    });
  }, [existing?.parentId, siteLockedToParent, selectedParent, sites, setValue]);

  const [createObject, { error: createError }] = useMutation(
    CREATE_OBJECT_MUTATION,
    {
      refetchQueries: [{ query: GET_MY_OBJECTS_QUERY }],
    }
  );
  const [updateObject, { error: updateError }] = useMutation(
    UPDATE_OBJECT_MUTATION,
    {
      refetchQueries: [{ query: GET_MY_OBJECTS_QUERY }],
      update(cache, { data }) {
        const result = data?.updateObject;
        if (result?.__typename !== 'UpdateObjectSuccess' || !result.object) {
          return;
        }
        const updated = result.object;
        try {
          const cached = cache.readQuery<{
            getMyObjects: ObjectSourceRecord[];
          }>({ query: GET_MY_OBJECTS_QUERY });
          if (!cached?.getMyObjects) return;
          cache.writeQuery({
            query: GET_MY_OBJECTS_QUERY,
            data: {
              getMyObjects: cached.getMyObjects.map((o) =>
                o.id === updated.id ? { ...o, ...updated } : o
              ),
            },
          });
        } catch {
          // Query not in cache yet; refetch will populate it.
        }
      },
    }
  );
  const [deleteObject, { loading: deleting }] = useMutation(
    DELETE_OBJECT_MUTATION,
    {
      refetchQueries: [{ query: GET_MY_OBJECTS_QUERY }],
    }
  );

  const draftGeometry: Geometry | null =
    (objectDraft as Feature | null)?.geometry ??
    existing?.geometry ??
    existing?.position ??
    null;
  const center = computeCenter(draftGeometry);
  const shapeMessage = draftGeometry
    ? geometryMessages[draftGeometry.type as keyof typeof geometryMessages]
    : null;
  const shapeName = shapeMessage ? intl.formatMessage(shapeMessage) : null;

  const g = draftGeometry as any;
  const isValidDraft = (() => {
    if (!draftGeometry) return false;
    switch (activeMapTool) {
      case MapTools.OBJECT_DRAW_POINT:
        return g.type === 'Point';
      case MapTools.OBJECT_DRAW_LINE:
        return g.type === 'LineString' && g.coordinates.length >= 2;
      case MapTools.OBJECT_DRAW_POLYGON:
        return g.type === 'Polygon' && (g.coordinates?.[0]?.length ?? 0) >= 4;
      case MapTools.OBJECT_DRAW_CIRCLE:
        return g.type === 'Polygon' && (g.coordinates?.[0]?.length ?? 0) >= 4;
      default:
        return true;
    }
  })();
  const canSubmit = editingObjectId ? true : isValidDraft;

  const drawHint = (() => {
    switch (activeMapTool) {
      case MapTools.OBJECT_DRAW_POINT:
        return draftGeometry
          ? intl.formatMessage({
              id: 'map.objects.hint.movePoint',
              defaultMessage: 'Click again to move the point.',
            })
          : intl.formatMessage({
              id: 'map.objects.hint.placePoint',
              defaultMessage: 'Click on the map to place the point.',
            });
      case MapTools.OBJECT_DRAW_LINE: {
        const n = g?.type === 'LineString' ? g.coordinates.length : 0;
        return n < 2
          ? intl.formatMessage(
              {
                id: 'map.objects.hint.lineMinimum',
                defaultMessage:
                  'Click on the map to add vertices ({count}/2 minimum).',
              },
              { count: n }
            )
          : intl.formatMessage(
              {
                id: 'map.objects.hint.lineReady',
                defaultMessage:
                  'Line has {count, plural, one {# vertex} other {# vertices}}. Click to add more, or save.',
              },
              { count: n }
            );
      }
      case MapTools.OBJECT_DRAW_POLYGON: {
        const n =
          g?.type === 'Polygon'
            ? g.coordinates[0].length - 1
            : g?.type === 'LineString'
            ? g.coordinates.length
            : 0;
        return n < 3
          ? intl.formatMessage(
              {
                id: 'map.objects.hint.polygonMinimum',
                defaultMessage:
                  'Click on the map to add vertices ({count}/3 minimum).',
              },
              { count: n }
            )
          : intl.formatMessage(
              {
                id: 'map.objects.hint.polygonReady',
                defaultMessage:
                  'Polygon has {count, plural, one {# vertex} other {# vertices}}. Click to add more, or save.',
              },
              { count: n }
            );
      }
      case MapTools.OBJECT_DRAW_CIRCLE:
        if (!draftGeometry) {
          return intl.formatMessage({
            id: 'map.objects.hint.circleCenter',
            defaultMessage: 'Click on the map to set the circle centre.',
          });
        }
        if (g?.type === 'Point') {
          return intl.formatMessage({
            id: 'map.objects.hint.circleEdge',
            defaultMessage: 'Click to set the circle edge.',
          });
        }
        return intl.formatMessage({
          id: 'map.objects.hint.circleRedraw',
          defaultMessage: 'Circle drawn. Click centre again to redraw.',
        });
      default:
        return null;
    }
  })();

  const onCancel = () => {
    clearObjectDraft();
    setActiveMapTool(null);
    setSelectedObject(null);
  };

  const handleCopyId = async () => {
    if (!editingObjectId) return;
    try {
      await navigator.clipboard.writeText(editingObjectId);
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 1500);
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  const handleDelete = async () => {
    if (!editingObjectId) return;
    if (
      !window.confirm(
        intl.formatMessage({
          id: 'map.objects.confirmDelete',
          defaultMessage: 'Delete this object?',
        })
      )
    ) {
      return;
    }
    await deleteObject({ variables: { id: editingObjectId } });
    setSelectedObject(null);
    clearObjectDraft();
    setActiveMapTool(null);
  };

  const submit: SubmitHandler<FormValues> = async (values) => {
    const { objectDraft: draft, editingObjectId: editId } = getWorkspaceStore();

    const geometryInput =
      editId && draft?.geometry
        ? { geometry: draft.geometry as any }
        : !editId && draft?.geometry
        ? { geometry: draft.geometry as any }
        : !editId && draftGeometry
        ? { geometry: draftGeometry as any }
        : {};

    const derivedSiteId = values.isParent
      ? values.site?.id ?? null
      : values.parent
      ? values.parent.siteId ?? null
      : values.site?.id ?? null;

    const input: Record<string, unknown> = {
      name: values.name || undefined,
      description: values.description || undefined,
      type: values.type || undefined,
      groupId: values.isParent ? null : values.group?.id ?? null,
      parentId: values.isParent ? null : values.parent?.id ?? null,
      mapsVisible: values.mapsVisible.length ? values.mapsVisible : null,
      filled: values.isParent ? true : values.filled,
      ...geometryInput,
    };

    if (!editId || dirtyFields.site || dirtyFields.parent) {
      input.siteId = derivedSiteId;
    }

    if (editId) {
      const result = await updateObject({
        variables: { id: editId, object: input },
      });
      const payload = result.data?.updateObject;
      if (payload?.__typename === 'ValidationError') {
        throw new Error(
          payload.errors?.map((e: { error: string }) => e.error).join(', ') ||
            intl.formatMessage({
              id: 'map.objects.saveError',
              defaultMessage: 'Failed to save object',
            })
        );
      }
    } else {
      await createObject({ variables: { object: input } });
    }

    clearObjectDraft();
    setActiveMapTool(null);
    setSelectedObject(null);
  };

  const onSubmit = handleSubmit(submit);

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mt: 1,
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle2">
            {editingObjectId ? (
              <FormattedMessage
                id="map.objects.edit"
                defaultMessage="Edit object"
              />
            ) : (
              <FormattedMessage
                id="map.objects.new"
                defaultMessage="New object"
              />
            )}
            {shapeName ? ` (${shapeName})` : ''}
          </Typography>
          {editingObjectId && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Fade in={idCopied} timeout={150}>
                <Typography sx={idCopiedStyles}>
                  <FormattedMessage
                    id="map.objects.idCopied"
                    defaultMessage="ID copied"
                  />
                </Typography>
              </Fade>
              {existing && (
                <Tooltip
                  arrow
                  placement="left"
                  title={<InfoTooltipCard existing={existing} />}
                  componentsProps={infoTooltipSlotProps}
                >
                  <IconButton
                    size="small"
                    aria-label={intl.formatMessage({
                      id: 'map.objects.info',
                      defaultMessage: 'Object info',
                    })}
                    sx={infoButtonStyles}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip
                title={intl.formatMessage({
                  id: 'map.objects.copyId',
                  defaultMessage: 'Copy ID to clipboard',
                })}
              >
                <IconButton
                  size="small"
                  onClick={handleCopyId}
                  aria-label={intl.formatMessage({
                    id: 'map.objects.copyId',
                    defaultMessage: 'Copy ID to clipboard',
                  })}
                  sx={copyButtonStyles}
                >
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>

        {drawHint && (
          <Typography variant="caption" color="text.secondary">
            {drawHint}
          </Typography>
        )}

        <FormField clampLabel>
          <FieldLabel htmlFor="object-name">
            <FormattedMessage id="form.name" defaultMessage="Name" />{' '}
            <RequiredLabel />
          </FieldLabel>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                id="object-name"
                surface="sunken"
                sx={inputStyles}
                {...field}
              />
            )}
          />
        </FormField>

        <FormField clampLabel>
          <FieldLabel htmlFor="object-description">
            <FormattedMessage
              id="form.description"
              defaultMessage="Description"
            />
          </FieldLabel>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                id="object-description"
                multiline
                minRows={2}
                maxRows={4}
                surface="sunken"
                sx={inputStyles}
                {...field}
              />
            )}
          />
        </FormField>

        <Controller
          name="isParent"
          control={control}
          render={({ field: { onChange, value } }) => (
            <SwitchLabel
              control={
                <Switch
                  checked={value}
                  onChange={(_, next) => onChange(next)}
                  tone="info"
                />
              }
              label={
                <FormattedMessage
                  id="map.objects.parentObject"
                  defaultMessage="Parent object"
                />
              }
              sx={switchLabelStyles}
            />
          )}
        />

        <FormField clampLabel>
          <FieldLabel htmlFor="object-type">
            <FormattedMessage id="form.type" defaultMessage="Type" />{' '}
            <RequiredLabel />
          </FieldLabel>
          <Controller
            name="type"
            control={control}
            render={({ field: { onChange, value, ref } }) => (
              <Autocomplete
                disablePortal
                id="object-type"
                size="small"
                surface="sunken"
                options={typeOptions}
                value={value}
                onChange={(_, next) => onChange(next)}
                getOptionLabel={typeLabelFor}
                renderInput={(params) => (
                  <TextField
                    inputRef={ref}
                    {...params}
                    surface="sunken"
                    placeholder={
                      isParent
                        ? intl.formatMessage({
                            id: 'map.objects.selectParentType',
                            defaultMessage: 'Select parent type',
                          })
                        : intl.formatMessage({
                            id: 'map.objects.selectType',
                            defaultMessage: 'Select type',
                          })
                    }
                    sx={inputStyles}
                  />
                )}
                sx={inputStyles}
              />
            )}
          />
        </FormField>

        <FormField clampLabel>
          <FieldLabel htmlFor="object-site">
            <FormattedMessage id="form.site" defaultMessage="Site" />
            {siteLockedToParent && (
              <>
                {' '}
                <FormattedMessage
                  id="map.objects.fromParent"
                  defaultMessage="(from parent)"
                />
              </>
            )}
          </FieldLabel>
          <Controller
            name="site"
            control={control}
            render={({ field: { onChange, value, ref } }) => (
              <Autocomplete
                disablePortal
                id="object-site"
                size="small"
                surface="sunken"
                disabled={siteLockedToParent || !canEditSite}
                options={sites}
                value={value}
                onChange={(_, next) => onChange(next)}
                getOptionLabel={(o) => o?.name ?? ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField
                    inputRef={ref}
                    {...params}
                    surface="sunken"
                    placeholder={
                      siteRestricted
                        ? intl.formatMessage({
                            id: 'map.objects.siteRestricted',
                            defaultMessage: 'Linked site not shared with you',
                          })
                        : siteLockedToParent
                        ? intl.formatMessage({
                            id: 'map.objects.parentHasNoSite',
                            defaultMessage: 'None (parent has no site)',
                          })
                        : intl.formatMessage({
                            id: 'common.none',
                            defaultMessage: 'None',
                          })
                    }
                    sx={inputStyles}
                  />
                )}
                sx={inputStyles}
              />
            )}
          />
          {siteRestricted && (
            <Typography variant="caption" color="text.secondary">
              <FormattedMessage
                id="map.objects.siteRestricted"
                defaultMessage="Linked site not shared with you"
              />
            </Typography>
          )}
        </FormField>

        {!isParent && (
          <FormField clampLabel>
            <FieldLabel htmlFor="object-group">
              <FormattedMessage id="form.group" defaultMessage="Group" />
            </FieldLabel>
            <Controller
              name="group"
              control={control}
              render={({ field: { onChange, value, ref } }) => (
                <Autocomplete
                  disablePortal
                  id="object-group"
                  size="small"
                  surface="sunken"
                  options={groups}
                  value={value}
                  onChange={(_, next) => onChange(next)}
                  getOptionLabel={(o) => o?.name ?? ''}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(params) => (
                    <TextField
                      inputRef={ref}
                      {...params}
                      surface="sunken"
                      placeholder={intl.formatMessage({
                        id: 'common.none',
                        defaultMessage: 'None',
                      })}
                      sx={inputStyles}
                    />
                  )}
                  sx={inputStyles}
                />
              )}
            />
          </FormField>
        )}

        {!isParent && (
          <FormField clampLabel>
            <FieldLabel htmlFor="object-parent">
              <FormattedMessage id="form.parent" defaultMessage="Parent" />
            </FieldLabel>
            <Controller
              name="parent"
              control={control}
              render={({ field: { onChange, value, ref } }) => (
                <Autocomplete
                  disablePortal
                  id="object-parent"
                  size="small"
                  surface="sunken"
                  options={parentOptions}
                  value={value}
                  onChange={(_, next) => onChange(next)}
                  getOptionLabel={(o) => o?.name ?? ''}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(params) => (
                    <TextField
                      inputRef={ref}
                      {...params}
                      surface="sunken"
                      placeholder={intl.formatMessage({
                        id: 'common.none',
                        defaultMessage: 'None',
                      })}
                      sx={inputStyles}
                    />
                  )}
                  sx={inputStyles}
                />
              )}
            />
          </FormField>
        )}

        <FormField clampLabel>
          <FieldLabel htmlFor="object-maps-visible">
            <FormattedMessage
              id="map.objects.mapsVisible"
              defaultMessage="Maps visible"
            />
          </FieldLabel>
          <Controller
            name="mapsVisible"
            control={control}
            render={({ field: { onChange, value, ref } }) => (
              <Stack spacing={0.75}>
                <Autocomplete
                  disablePortal
                  id="object-maps-visible"
                  size="small"
                  multiple
                  surface="sunken"
                  options={layerKeys}
                  value={value}
                  onChange={(_, next) => onChange(next)}
                  getOptionLabel={getLayerLabel}
                  renderTags={() => null}
                  renderInput={(params) => (
                    <TextField
                      inputRef={ref}
                      {...params}
                      surface="sunken"
                      placeholder={intl.formatMessage({
                        id: 'map.objects.allLayers',
                        defaultMessage: 'All layers',
                      })}
                      sx={inputStyles}
                    />
                  )}
                  sx={inputStyles}
                />
                {value.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {value.map((layerKey) => (
                      <Chip
                        key={layerKey}
                        size="small"
                        label={getLayerLabel(layerKey)}
                        onDelete={() =>
                          onChange(value.filter((v) => v !== layerKey))
                        }
                      />
                    ))}
                  </Box>
                )}
              </Stack>
            )}
          />
        </FormField>

        {!isParent && (
          <Controller
            name="filled"
            control={control}
            render={({ field: { onChange, value } }) => (
              <SwitchLabel
                control={
                  <Switch
                    checked={value}
                    onChange={(_, next) => onChange(next)}
                  />
                }
                label={
                  <FormattedMessage
                    id="map.objects.filled"
                    defaultMessage="Filled"
                  />
                }
                sx={switchLabelStyles}
              />
            )}
          />
        )}

        {center && (
          <Typography variant="caption" color="text.secondary">
            <FormattedMessage
              id="map.objects.center"
              defaultMessage="Center: {latitude}, {longitude}"
              values={{
                latitude: center.lat.toFixed(5),
                longitude: center.lng.toFixed(5),
              }}
            />
          </Typography>
        )}

        {(createError || updateError) && (
          <Alert severity="error" variant="outlined">
            {createError?.message || updateError?.message}
          </Alert>
        )}

        {editingObjectId && (
          <CondensedDangerZoneAction
            actionIcon={<DeleteIcon fontSize="small" />}
            actionLabel={
              <FormattedMessage
                id="map.objects.delete"
                defaultMessage="Delete object"
              />
            }
            background="sunken"
            disabled={deleting}
            label={
              <FormattedMessage
                id="form.dangerZone"
                defaultMessage="Danger zone"
              />
            }
            onAction={handleDelete}
          />
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            density="compact"
            tone="neutral"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            <FormattedMessage id="action.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            density="compact"
            type="submit"
            disabled={!canSubmit || isSubmitting}
          >
            {editingObjectId ? (
              <FormattedMessage id="action.save" defaultMessage="Save" />
            ) : (
              <FormattedMessage id="action.create" defaultMessage="Create" />
            )}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export { ObjectForm };
